// Canonical SWR keys for transcript / resume / recording data. Every reader,
// writer (via mutate), and the bulk seeder import from here so they all agree
// on the exact key shape. Array keys (not template strings) let SWR compare
// field-by-field.

export type TranscriptKey = readonly ['transcript', string, string];
export type TranscriptAvailabilityKey = readonly [
  'transcript-availability',
  string,
  string,
  'authenticated' | 'public-share',
];
export type TranscriptHistoryKey = readonly [
  'transcript-history',
  string,
  string,
];
export type TranscriptReviewKey = readonly [
  'transcript-review',
  string,
  string,
];
export type TranscriptRecommendationsKey = readonly [
  'transcript-recommendations',
  string,
  string,
];
export type TranscriptPriorInterviewsKey = readonly [
  'transcript-prior-interviews',
  string,
  string,
];
export type TranscriptCommunicationsKey = readonly [
  'transcript-communications',
  string,
  string,
];
export type TranscriptEmailCommunicationsKey = readonly [
  'transcript-email-communications',
  string,
  string,
];
export type TranscriptAiCallerKey = readonly ['transcript-ai-caller', string];
export type TranslationKey = readonly [
  'transcript-translation',
  string,
  string,
  string,
  string,
  string,
];
export type ResumeUrlKey = readonly ['resume-url', string, string, string];
export type ResumeDetailsKey = readonly ['resume-details', string, string];
export type RecordingKey = readonly ['recording', string];
export type TimelineKey = readonly ['timeline', string];
export type PersonaVerificationKey = readonly [
  'persona-verification',
  string,
  string,
];
export type IdVerificationKey = readonly ['id-verification', string, string];
export type FraudSignalsKey = readonly ['fraud-signals', string, string];
export type SharedContactMatchesKey = readonly [
  'shared-contact-matches',
  string,
  string,
];
export type FeedbackKey = readonly ['feedback', string, string];
export type CandidateNotesKey = readonly ['candidate-notes', string, string];
export type CandidateCommentsKey = readonly [
  'candidate-comments',
  string,
  string,
];
export type SupportCallDetailsKey = readonly [
  'support-call-details',
  string,
  string,
  string,
];
export type ScheduleFollowUpEmailsKey = readonly [
  'campaign-schedule-follow-up-emails',
  string,
];
export type WebCallSettingsKey =
  | readonly ['web-call-settings', string]
  | readonly ['web-call-settings', string, string, string]
  | readonly ['web-call-settings', string, string, string, string];

export interface WebCallSettingsKeyParams {
  originalInterviewCampaignId?: string | null;
  uiLang?: string | null;
}
export type IpGeolocationKey = readonly ['ip-geolocation', string];
export type CandidateTagsKey = readonly ['candidate-tags', string];
export type TranscriptAccessKey = readonly [
  'transcript-access',
  string,
  string,
  string,
];

// Returns null (SWR's "don't fetch" sentinel) until the required ids are present.
export const transcriptKey = (
  candidateId?: string,
  campaignId?: string
): TranscriptKey | null =>
  candidateId && campaignId ? ['transcript', candidateId, campaignId] : null;

const sectionKey = <S extends string>(
  section: S,
  candidateId?: string,
  campaignId?: string,
  enabled = true
): readonly [S, string, string] | null =>
  enabled && candidateId && campaignId
    ? [section, candidateId, campaignId]
    : null;

export const transcriptAvailabilityKey = (
  candidateId?: string,
  campaignId?: string,
  viewerMode: 'authenticated' | 'public-share' = 'authenticated',
  enabled = true
): TranscriptAvailabilityKey | null =>
  enabled && candidateId && campaignId
    ? ['transcript-availability', candidateId, campaignId, viewerMode]
    : null;

/** True for every key `transcriptAvailabilityKey` builds. */
export const isTranscriptAvailabilityKey = (
  key: unknown
): key is TranscriptAvailabilityKey =>
  Array.isArray(key) && key[0] === 'transcript-availability';

export const transcriptHistoryKey = (
  candidateId?: string,
  campaignId?: string,
  enabled = true
): TranscriptHistoryKey | null =>
  sectionKey('transcript-history', candidateId, campaignId, enabled);

export const transcriptReviewKey = (
  candidateId?: string,
  campaignId?: string,
  enabled = true
): TranscriptReviewKey | null =>
  sectionKey('transcript-review', candidateId, campaignId, enabled);

export const transcriptRecommendationsKey = (
  candidateId?: string,
  campaignId?: string,
  enabled = true
): TranscriptRecommendationsKey | null =>
  sectionKey('transcript-recommendations', candidateId, campaignId, enabled);

export const transcriptPriorInterviewsKey = (
  candidateId?: string,
  campaignId?: string,
  enabled = true
): TranscriptPriorInterviewsKey | null =>
  sectionKey('transcript-prior-interviews', candidateId, campaignId, enabled);

export const transcriptCommunicationsKey = (
  candidateId?: string,
  campaignId?: string,
  enabled = true
): TranscriptCommunicationsKey | null =>
  sectionKey('transcript-communications', candidateId, campaignId, enabled);

export const transcriptEmailCommunicationsKey = (
  candidateId?: string,
  campaignId?: string,
  enabled = true
): TranscriptEmailCommunicationsKey | null =>
  sectionKey(
    'transcript-email-communications',
    candidateId,
    campaignId,
    enabled
  );

export const transcriptAiCallerKey = (
  campaignId?: string,
  enabled = true
): TranscriptAiCallerKey | null =>
  enabled && campaignId ? ['transcript-ai-caller', campaignId] : null;

// Translations live under their own key (not the transcript entry) so a
// transcript revalidation can't overwrite a cached translation. callId +
// revision keep edits and call switches from serving a stale translation.
export const translationKey = (
  candidateId?: string,
  campaignId?: string,
  callId?: string,
  revision?: string,
  targetLanguage?: string
): TranslationKey | null =>
  candidateId &&
  campaignId &&
  callId &&
  revision !== undefined &&
  targetLanguage !== undefined
    ? [
        'transcript-translation',
        candidateId,
        campaignId,
        callId,
        revision,
        targetLanguage,
      ]
    : null;

// callId participates because a submittal report decides per call whether its resume is
// disclosed, so two reports for the same candidate and job can legitimately differ.
export const resumeUrlKey = (
  candidateId?: string,
  campaignId?: string,
  callId?: string
): ResumeUrlKey | null =>
  candidateId
    ? ['resume-url', candidateId, campaignId ?? '', callId ?? '']
    : null;

export const resumeDetailsKey = (
  candidateId?: string,
  campaignId?: string
): ResumeDetailsKey | null =>
  candidateId && campaignId
    ? ['resume-details', candidateId, campaignId]
    : null;

export const recordingKey = (callId?: string): RecordingKey | null =>
  callId ? ['recording', callId] : null;

// Each key below gates a transcript-drawer data hook. The drawer reuses its
// subtree across candidates, so a stable per-input key (plus SWR's stale-
// response discarding) is what prevents one candidate's data from rendering
// under another after a fast switch.
export const timelineKey = (
  callId?: string,
  enabled?: boolean
): TimelineKey | null => (enabled && callId ? ['timeline', callId] : null);

export const personaVerificationKey = (
  candidateId?: string,
  callId?: string,
  webCall?: boolean
): PersonaVerificationKey | null =>
  webCall && candidateId && callId
    ? ['persona-verification', candidateId, callId]
    : null;

// Persona verification attempts plus face-match sessions for a call. Not gated on
// webCall: the ID tab also renders for phone screens when fraud signals are on.
export const idVerificationKey = (
  candidateId?: string,
  callId?: string,
  enabled = true
): IdVerificationKey | null =>
  enabled && candidateId && callId
    ? ['id-verification', candidateId, callId]
    : null;

// Not gated on webCall: fraud signals are candidate-level and apply to phone
// screens as much as web interviews (e.g. a VOIP phone-number flag).
export const fraudSignalsKey = (
  candidateId?: string,
  campaignId?: string
): FraudSignalsKey | null =>
  candidateId && campaignId ? ['fraud-signals', candidateId, campaignId] : null;

// Gated on `expanded` so a cluster is only fetched once the reviewer opens it.
export const sharedContactMatchesKey = (
  candidateId?: string,
  signalId?: string,
  expanded?: boolean
): SharedContactMatchesKey | null =>
  expanded && candidateId && signalId
    ? ['shared-contact-matches', candidateId, signalId]
    : null;

export const feedbackKey = (
  candidateId?: string,
  campaignId?: string
): FeedbackKey | null =>
  candidateId && campaignId ? ['feedback', candidateId, campaignId] : null;

export const candidateNotesKey = (
  candidateId?: string,
  campaignId?: string
): CandidateNotesKey | null =>
  candidateId && campaignId
    ? ['candidate-notes', candidateId, campaignId]
    : null;

export const candidateCommentsKey = (
  candidateId?: string,
  campaignId?: string,
  enabled = true
): CandidateCommentsKey | null =>
  enabled && candidateId && campaignId
    ? ['candidate-comments', candidateId, campaignId]
    : null;

export const scheduleFollowUpEmailsKey = (
  campaignId?: string,
  enabled = true
): ScheduleFollowUpEmailsKey | null =>
  enabled && campaignId
    ? ['campaign-schedule-follow-up-emails', campaignId]
    : null;

// Param-less callers (the transcript gates, FeedbackPage) share one cache
// entry. A translated-policy fetch (uiLang / originalInterviewCampaignId: the
// interview and scheduling pages) is genuinely different data, so it keys on
// those params instead of colliding.
export const webCallSettingsKey = (
  campaignId?: string,
  params?: WebCallSettingsKeyParams,
  sessionId?: string
): WebCallSettingsKey | null => {
  if (!campaignId) return null;
  if (sessionId) {
    return [
      'web-call-settings',
      campaignId,
      params?.originalInterviewCampaignId ?? '',
      params?.uiLang ?? '',
      sessionId,
    ];
  }
  if (!params || (!params.originalInterviewCampaignId && !params.uiLang)) {
    return ['web-call-settings', campaignId];
  }
  return [
    'web-call-settings',
    campaignId,
    params.originalInterviewCampaignId ?? '',
    params.uiLang ?? '',
  ];
};

export const ipGeolocationKey = (
  ipAddress?: string
): IpGeolocationKey | null =>
  ipAddress && ipAddress !== 'unknown' ? ['ip-geolocation', ipAddress] : null;

export const transcriptAccessKey = (
  candidateId?: string,
  campaignId?: string,
  orgId?: string
): TranscriptAccessKey | null =>
  candidateId && campaignId
    ? ['transcript-access', candidateId, campaignId, orgId ?? '']
    : null;

export const candidateTagsKey = (
  candidateId?: string
): CandidateTagsKey | null =>
  candidateId ? ['candidate-tags', candidateId] : null;

// `callsSignature` changes when a call is added or its status/update time moves, so the
// support drawer refetches details for calls that appear or finish while it is open.
export const supportCallDetailsKey = (
  candidateId?: string,
  campaignId?: string,
  callsSignature = '',
  enabled = true
): SupportCallDetailsKey | null =>
  enabled && candidateId && campaignId
    ? ['support-call-details', candidateId, campaignId, callsSignature]
    : null;
